import React from "react";
import { Box, Container, Link, Typography } from "@mui/material";

const CONTACT_EMAIL = "blacksmith@titanforged.com";

const Section = ({ title, children }) => (
  <Box component="section" sx={{ mt: 4 }}>
    <Typography variant="h5" component="h2" gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

const Paragraph = ({ children }) => (
  <Typography variant="body1" paragraph>
    {children}
  </Typography>
);

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ pl: 3, mt: 0, mb: 2 }}>
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </Box>
);

const GymCompanionPrivacyPage = () => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        Privacy Policy
      </Typography>
      <Typography variant="h6" component="p" gutterBottom>
        Gym Companion for Android
      </Typography>
      <Typography variant="subtitle1" component="p" sx={{ fontWeight: 700 }} paragraph>
        Effective Date: August 19, 2026
      </Typography>

      <Paragraph>
        Gym Companion is a personal fitness and training log developed and
        provided by <strong>TitanForged</strong>.
      </Paragraph>
      <Paragraph>
        Gym Companion is designed to keep your information private and under
        your control. The app does not require an account, does not
        synchronize your fitness data with a Gym Companion or TitanForged
        server, and does not sell your personal information.
      </Paragraph>
      <Paragraph>
        Information you enter into Gym Companion is stored locally on your
        device unless you choose to export or share it.
      </Paragraph>

      <Section title="Scope of This Policy">
        <Paragraph>
          This Privacy Policy applies to the{" "}
          <strong>Gym Companion Android application</strong> developed and
          provided by TitanForged.
        </Paragraph>
        <Paragraph>
          This policy describes how information is handled by the Gym Companion
          app. It does not apply to unrelated TitanForged websites,
          applications, or services.
        </Paragraph>
      </Section>

      <Section title="Information Stored by Gym Companion">
        <Paragraph>
          Gym Companion allows you to voluntarily record information related to
          your workouts, fitness, nutrition, supplements, and body measurements.
        </Paragraph>
        <Paragraph>
          Depending on how you use the app, this may include:
        </Paragraph>
        <BulletList
          items={[
            "Workout sessions, exercises, sets, repetitions, and weights",
            "Cardio activities, intervals, distances, durations, resistance levels, and incline settings",
            "Workout notes",
            "Workout templates and your exercise list",
            "Optional photos attached to exercises",
            "Protein intake and daily protein goals",
            "Supplement schedules and intake status",
            "Body measurements such as weight, waist, and midriff measurements",
            "Daily notes",
            "Application preferences such as measurement units and display settings",
          ]}
        />
        <Paragraph>
          Some information you choose to enter may be considered health or
          fitness information.
        </Paragraph>
        <Paragraph>
          This information is stored locally within the app's storage on your
          Android device.
        </Paragraph>
      </Section>

      <Section title="Information Received by TitanForged">
        <Paragraph>
          <strong>
            TitanForged does not receive or collect the fitness, health,
            workout, supplement, body measurement, photo, or other personal
            information you enter into Gym Companion.
          </strong>
        </Paragraph>
        <Paragraph>
          Gym Companion does not require you to create an account and does not
          collect an email address, username, password, or other account
          credentials.
        </Paragraph>
        <Paragraph>
          The app does not transmit your Gym Companion database or logged
          fitness information to TitanForged servers.
        </Paragraph>
      </Section>

      <Section title="Analytics, Advertising, and Tracking">
        <Paragraph>Gym Companion does not use advertising services.</Paragraph>
        <Paragraph>
          Gym Companion does not use analytics services that transmit your app
          usage information to TitanForged.
        </Paragraph>
        <Paragraph>
          Gym Companion does not use third-party crash-reporting services that
          transmit your Gym Companion data to TitanForged.
        </Paragraph>
        <Paragraph>
          Gym Companion does not sell personal information or share personal
          information with advertisers.
        </Paragraph>
      </Section>

      <Section title="Data Sharing">
        <Paragraph>
          Gym Companion does not automatically share your information with
          third parties.
        </Paragraph>
        <Paragraph>
          You may choose to export, save, or share information from the app.
          When you do so, Android may allow you to select another application,
          storage provider, email service, cloud storage service, or other
          destination.
        </Paragraph>
        <Paragraph>
          Any transfer initiated by you is under your control. Information sent
          to another application or service becomes subject to that
          application's or service's privacy practices.
        </Paragraph>
        <Paragraph>
          TitanForged does not control data after you choose to export or share
          it with another party.
        </Paragraph>
      </Section>

      <Section title="Backup and Export Files">
        <Paragraph>
          Gym Companion allows you to create a local backup of your app data.
        </Paragraph>
        <Paragraph>
          A backup may contain your Gym Companion database, application
          preferences, and other information necessary to restore your data.
        </Paragraph>
        <Paragraph>
          Backup files created by Gym Companion are{" "}
          <strong>not encrypted by Gym Companion</strong>. You should treat
          these files as private documents.
        </Paragraph>
        <Paragraph>
          You choose where an exported backup is stored. For example, you may
          save it to your device, Downloads folder, another application, cloud
          storage provider, email service, or another device.
        </Paragraph>
        <Paragraph>
          Once a backup leaves Gym Companion's private app storage, its
          security and retention depend on the location or service you selected.
        </Paragraph>
        <Paragraph>
          Deleting or uninstalling Gym Companion does not automatically delete
          backup files that you previously exported.
        </Paragraph>
      </Section>

      <Section title="Restoring Backups">
        <Paragraph>
          You may restore a Gym Companion backup onto your device.
        </Paragraph>
        <Paragraph>
          Restoring a backup replaces the Gym Companion data currently stored
          by the app with the information contained in the selected backup.
        </Paragraph>
        <Paragraph>
          Backup restoration is performed locally on the device.
        </Paragraph>
      </Section>

      <Section title="Spreadsheet Imports">
        <Paragraph>
          Gym Companion may allow you to select a supported spreadsheet
          containing workout or related information for import.
        </Paragraph>
        <Paragraph>
          The selected file is processed locally on your device. Gym Companion
          does not upload the spreadsheet to TitanForged for processing.
        </Paragraph>
      </Section>

      <Section title="Android Backup and Device Transfer">
        <Paragraph>
          Automatic Android cloud backup of Gym Companion application data is
          disabled.
        </Paragraph>
        <Paragraph>
          Android or your device manufacturer may provide device-to-device
          migration or transfer features when setting up a new device. If you
          choose to use those features, Android may transfer Gym Companion's
          local application data to the new device.
        </Paragraph>
        <Paragraph>
          Such a transfer is performed as part of the Android or device
          migration process and is not a transfer of your information to
          TitanForged.
        </Paragraph>
      </Section>

      <Section title="Exercise Photos">
        <Paragraph>
          If you select a photo for an exercise, Gym Companion copies the
          selected image into the app's own storage so that the image remains
          available without depending on continued access to the original image
          in your photo library.
        </Paragraph>
        <Paragraph>
          Gym Companion does not upload these photos to TitanForged.
        </Paragraph>
        <Paragraph>
          You can remove an attached photo through the app where that
          functionality is available. Clearing Gym Companion's application data
          or uninstalling the app also removes photos stored within the app's
          private storage on that device.
        </Paragraph>
        <Paragraph>
          Copies that you independently exported, backed up, or stored
          elsewhere are not affected.
        </Paragraph>
      </Section>

      <Section title="Data Retention and Deletion">
        <Paragraph>
          Gym Companion retains information locally on your device for as long
          as you choose to keep it.
        </Paragraph>
        <Paragraph>
          TitanForged does not maintain a server-side copy of your Gym
          Companion data.
        </Paragraph>
        <Paragraph>You can:</Paragraph>
        <BulletList
          items={[
            "Edit or delete individual information using the app's available controls",
            "Remove attached exercise photos",
            "Clear Gym Companion's locally stored data",
            "Use Android's application storage controls to clear the app's data",
            "Uninstall Gym Companion",
          ]}
        />
        <Paragraph>
          Because Gym Companion does not maintain user accounts or a remote
          database containing your fitness information, TitanForged cannot
          remotely access, recover, modify, or delete information stored on
          your device.
        </Paragraph>
        <Paragraph>
          Exported backup files remain wherever you saved them until you delete
          them from that location.
        </Paragraph>
      </Section>

      <Section title="Security">
        <Paragraph>
          Gym Companion uses Android's application storage mechanisms to keep
          its local files isolated from other applications under normal Android
          security controls.
        </Paragraph>
        <Paragraph>
          Gym Companion does not transmit your workout, fitness, supplement,
          body measurement, or related app data to TitanForged servers.
        </Paragraph>
        <Paragraph>
          No method of electronic storage can guarantee absolute security. You
          are responsible for maintaining appropriate security for your device
          and for any unencrypted backup files you choose to export.
        </Paragraph>
        <Paragraph>
          Using a device screen lock and protecting exported backup files can
          help prevent unauthorized access to your information.
        </Paragraph>
      </Section>

      <Section title="Your Choices and Control">
        <Paragraph>
          Your Gym Companion information remains under your control.
        </Paragraph>
        <Paragraph>You may:</Paragraph>
        <BulletList
          items={[
            "Review and modify information stored in the app",
            "Delete individual records where supported",
            "Remove attached exercise photos",
            "Clear all locally stored Gym Companion data",
            "Export a backup of your data",
            "Delete previously exported backup files from the locations where you stored them",
            "Uninstall Gym Companion",
          ]}
        />
        <Paragraph>
          Because TitanForged does not receive or maintain your locally stored
          Gym Companion data, you do not need to contact TitanForged to request
          deletion of that data. It can be deleted directly from your device.
        </Paragraph>
      </Section>

      <Section title="Children">
        <Paragraph>
          Gym Companion is not directed to children under the age of 13.
        </Paragraph>
        <Paragraph>
          TitanForged does not knowingly collect personal information from
          children through Gym Companion.
        </Paragraph>
      </Section>

      <Section title="Privacy Policy Website">
        <Paragraph>
          This Privacy Policy may be hosted on the TitanForged website for
          public access.
        </Paragraph>
        <Paragraph>
          Viewing the privacy policy webpage is separate from using the Gym
          Companion application. The website or its hosting provider may
          process ordinary web-server information associated with visiting a
          webpage, such as an IP address, browser information, or request logs.
        </Paragraph>
        <Paragraph>
          The Gym Companion application does not use the privacy policy webpage
          to collect, upload, or synchronize your fitness information.
        </Paragraph>
      </Section>

      <Section title="Changes to This Privacy Policy">
        <Paragraph>
          Gym Companion's privacy practices may change as the application is
          updated.
        </Paragraph>
        <Paragraph>
          If this Privacy Policy changes, the revised policy will be published
          on this page and the effective date at the top of the policy will be
          updated.
        </Paragraph>
        <Paragraph>
          If a future version of Gym Companion materially changes how personal
          or sensitive information is collected, transmitted, used, or shared,
          this policy will be updated accordingly.
        </Paragraph>
      </Section>

      <Section title="Contact">
        <Paragraph>
          Questions regarding this Privacy Policy or Gym Companion may be
          directed to:
        </Paragraph>
        <Paragraph>
          <strong>TitanForged</strong>
          <br />
          <strong>Email:</strong>{" "}
          <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
        </Paragraph>
      </Section>
    </Container>
  );
};

export default GymCompanionPrivacyPage;
